import React, { useEffect, useMemo, useState } from "react";

import { formatNumber, useGetFieldVerifications, useGetVerificationEditingRequests } from "utils";

import { EDITING_REQUEST_TYPE, FIELD_VERIFICATION_FILTER } from "models";

import { Col, Icon, IconButton, InsightCard, Row, Table, TableFieldPanel, Tag, Typography } from "components";

import { FieldDrawer } from "pages/admin/verifications/components/drawers/FieldDrawer";
import { EditingRequestInsightCards } from "pages/admin/verifications/components/misc/EditingRequestInsightCards";
import { SearchAndFilter } from "pages/admin/verifications/components/misc/SearchAndFilter";
import { VerificationStatusDisplay } from "pages/admin/verifications/components/misc/VerificationStatusDisplay";
import { getFieldVerificationStatus, sortFields } from "pages/admin/verifications/utils/fieldVerificationUtils";

const FIELD_LEVEL_TYPES = [EDITING_REQUEST_TYPE.FIELD_SHAPE, EDITING_REQUEST_TYPE.FIELD_SPLIT, EDITING_REQUEST_TYPE.FIELD_REDZONE];

// Filter fields based on the selected filter (for field-level filters)
const filterFields = (fields, filter) => {
  if (!filter || filter === FIELD_VERIFICATION_FILTER.allFields) return fields;

  return fields.filter((field) => {
    switch (filter) {
      case FIELD_VERIFICATION_FILTER.pendingVerification:
        return field.verifications.some(({ verified }) => verified === null);

      // Editing request filters for fields (field-level)
      case FIELD_VERIFICATION_FILTER.hasFieldEditingRequests: {
        const { fieldLevel } = field.editingRequests;
        return fieldLevel.open + fieldLevel.toReview + fieldLevel.reviewed > 0;
      }

      case FIELD_VERIFICATION_FILTER.openEditingRequests:
        return field.editingRequests.fieldLevel.open > 0;

      case FIELD_VERIFICATION_FILTER.toReviewEditingRequests:
        return field.editingRequests.fieldLevel.toReview > 0;

      case FIELD_VERIFICATION_FILTER.reviewedEditingRequests:
        return field.editingRequests.fieldLevel.reviewed > 0;

      default:
        return true;
    }
  });
};

export const FieldList = ({ farm, farmId, summary, onUpdate, payoutApproved }) => (
  <>
    <Summary summary={summary} farmId={farmId} />
    <FieldTable payoutApproved={payoutApproved} farm={farm} farmId={farmId} onDataChange={onUpdate} />
  </>
);

const Summary = ({ summary, farmId }) => {
  const { data: editingRequests, isLoading: isLoadingEditingRequests } = useGetVerificationEditingRequests({ farmId, isAdmin: true });

  return (
    <>
      <Row className="padding-bottom-200">
        <Col sm={12} md={4}>
          <InsightCard loading={!summary} label="fields uploaded">
            {summary && summary.totalFieldCount - summary.manualFieldCount}
          </InsightCard>
        </Col>

        <Col sm={12} md={4}>
          <InsightCard loading={!summary} label="fields added manually">
            {summary?.manualFieldCount}
          </InsightCard>
        </Col>

        {summary?.fieldPendingVerificationCount !== 0 ? (
          <Col sm={24} md={4}>
            <InsightCard loading={!summary} className="yellow" label="completed verifications">
              {summary && (
                <>
                  {summary.totalFieldVerificationCount - summary.fieldPendingVerificationCount}/{summary.totalFieldVerificationCount}
                </>
              )}
            </InsightCard>
          </Col>
        ) : (
          <Col sm={24} md={4} style={{ display: "flex" }}>
            <InsightCard attach="right" label="approved" status="success">
              {summary.fieldApprovedVerifications}
            </InsightCard>
            <InsightCard attach="left" label={summary.fieldRejectedVerifications ? "rejected" : ""} status="alert">
              {summary.fieldRejectedVerifications || ""}
            </InsightCard>
          </Col>
        )}

        <EditingRequestInsightCards loading={isLoadingEditingRequests} editingRequests={editingRequests ?? []} requestTypes={FIELD_LEVEL_TYPES} />
      </Row>
    </>
  );
};

const FieldTable = ({ farm, farmId, onDataChange, payoutApproved }) => {
  const [page, setPage] = useState(1);
  const defaultItemsPerPage = 20;
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [filter, setFilter] = useState();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState();
  const [selectedFieldId, setSelectedFieldId] = useState();
  const [selectedField, setSelectedField] = useState();
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedFieldVersion, setSelectedFieldVersion] = useState(0);

  // Fetch all fields once (filtering/sorting/pagination done locally)
  const { data: fieldData, isLoading } = useGetFieldVerifications({
    farmId,
    search: search || undefined,
    filter: filter === FIELD_VERIFICATION_FILTER.allFields ? filter : undefined,
  });

  // Fetch editing requests for the farm (for drawer)
  const { data: editingRequests } = useGetVerificationEditingRequests({ farmId, isAdmin: true });

  // Apply local filtering and sorting
  const processedFields = useMemo(() => {
    if (!fieldData?.fields) return [];
    const filtered = filterFields(fieldData.fields, filter);
    const sorted = sortFields(filtered, sort);
    return sorted;
  }, [fieldData?.fields, filter, sort]);

  // Helper function to get editing request status for a field (using the new metadata)
  const getFieldEditingRequestStatus = (field) => {
    const { fieldLevel } = field.editingRequests;

    if (fieldLevel.open > 0) {
      return { text: "open request", colour: "red" };
    }
    if (fieldLevel.toReview > 0) {
      return { text: "review request", colour: "orange" };
    }
    if (fieldLevel.reviewed > 0) {
      return { text: "reviewed", colour: "blue" };
    }
    return null;
  };

  // Helper function to get field-level editing requests for drawer
  const getFieldEditingRequests = (fieldId) =>
    editingRequests?.filter((request) => request.fieldId === fieldId && FIELD_LEVEL_TYPES.includes(request.requestType)) ?? [];

  const handlePageChange = (newPage, pageSize) => {
    setPage(newPage);
    setItemsPerPage(pageSize);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1);
  };

  const handleSortChange = (newSort) => {
    setSort(newSort);
    setPage(1);
  };

  const handleDrawerOpen = (field) => {
    setSelectedFieldId(field.id);
    setShowDrawer(true);
  };

  const handleDrawerClose = () => {
    setShowDrawer(false);
    setTimeout(() => setSelectedFieldId(null), 200);
  };

  const data = processedFields.map((field) => ({
    key: field.id,
    "field name": (
      <TableFieldPanel
        field={field}
        badges={
          field.commentCount > 0 && (
            <>
              <Icon iconName={"tabler/message-2"} className="icon inline" size="s" />
              {field.commentCount}
            </>
          )
        }
        tag={getFieldVerificationStatus(field.verifications) === "pending" && <Tag text="pending verifications" colour="yellow" />}
        onClick={() => handleDrawerOpen(field)}
        isAdmin
      />
    ),
    size: <Typography variant="paragraph-xs">{formatNumber({ number: parseFloat(field.size), decimalPlaces: 4 })}ha</Typography>,
    redzone: field.redzone && (
      <Tag colour={field.redzone.confirmed ? "blue" : "red"} text={field.redzone.confirmed ? "confirmed redzone" : "redzone warning"} />
    ),
    "editing request": (() => {
      const editingRequestStatus = getFieldEditingRequestStatus(field);
      return editingRequestStatus ? <Tag colour={editingRequestStatus.colour} text={editingRequestStatus.text} /> : null;
    })(),
    "verification status": <VerificationStatusDisplay status={getFieldVerificationStatus(field?.verifications)} />,
    " ": (
      <IconButton
        className="margin-left-50"
        type="secondary"
        iconName="tabler/eye"
        tooltip={{ title: "Open field drawer", placement: "top" }}
        onClick={() => handleDrawerOpen(field)}
      />
    ),
  }));

  useEffect(() => {
    if (!selectedFieldId) {
      setSelectedField(null);
      return;
    }

    const newSelectedField = fieldData?.fields.find(({ id }) => id === selectedFieldId);
    setSelectedField((oldSelectedField) => newSelectedField ?? oldSelectedField);
    setSelectedFieldVersion((prev) => prev + 1);
  }, [selectedFieldId, fieldData?.fields]);

  return (
    <>
      <SearchAndFilter setFilter={handleFilterChange} setPage={setPage} setSearch={setSearch} setSort={handleSortChange} type="field" />
      <Row>
        <Col>
          {!isLoading && processedFields.length === 0 && <Typography variant="paragraph-m">There are no fields to verify.</Typography>}
          <Table
            data={data ?? []}
            loading={isLoading}
            className="verification-field-table"
            pagination={{
              onChange: handlePageChange,
              total: processedFields.length,
              current: page,
              pageSizeOptions: [10, 20, 50, 100],
              defaultPageSize: defaultItemsPerPage,
              pageSize: itemsPerPage,
            }}
          />
        </Col>
      </Row>
      {selectedField && (
        <FieldDrawer
          field={selectedField}
          farm={farm}
          farmId={farmId}
          visible={showDrawer}
          loadingFields={isLoading}
          onClose={handleDrawerClose}
          onDataChange={onDataChange}
          payoutApproved={payoutApproved}
          selectedFieldVersion={selectedFieldVersion}
          editingRequests={getFieldEditingRequests(selectedField.id)}
        />
      )}
    </>
  );
};
